import {
  convertTime12,
  convertToYearMonthDay,
  currentDateTime,
  parseFromUserTimeZone,
} from '@/Core/filters/dates';
import formatContactName from '@/Core/filters/formatContactName';
import { feathers } from '@/feathers-client';
import { TAB_V1, TAB_V21 } from '@/Tasks/config/constants';
import axios from 'axios';
import { ESSearchHeader } from '@/Core/config/es-search-header';

const DESCENDING = '-1';
const ASCENDING = '1';

const PREVIOUS_DAYS = 1;
const UPCOMING_DAYS = 2;

// Max array size for /contacts call with id: $in[contact] param
const MAX_CONTACT_ARRAY_LENGTH = 20;

const ACTION_TYPES = {
  None: 0,
  'Email Contact': 1,
  'Call Contact': 2,
  'Text Contact': 3,
  'Agent Task - Email Reminder': 4,
  'Agent Task - Call Reminder': 5,
  'Agent Task - Text Reminder': 6,
  'Agent Task - No Reminder': 7,
  'BombBomb Video Email': 8,
  'Direct Mail: Letter': 9,
  'Direct Mail: Postcard': 10,
  'Agent Task - Direct Mail: Letter (Manual)': 11,
  'Agent Task - Direct Mail: Postcard (Manual)': 12,
  'LionDesk Lead Assist (TM)': 13,
};

const TASKS_ACTIONS = {
  calendar: 'tasks',
  campaign: 'campaign_tasks',
  nylas: 'nylas_tasks',
};

// todo bk: this should live somewhere else
const categoryNone = { id: 0, name: 'None', rank: 0, color: '#ccc' };

const applyFilters = (query, filter) => {
  query.status = 'incomplete';
  switch (filter) {
    case 'overdue': {
      query.due_at = {
        $lte: new Date(),
      };
      break;
    }
    case 'current': {
      const now = currentDateTime();

      const currentTasksRangeStartDate = new Date(now.getTime());
      currentTasksRangeStartDate.setHours(0, 0, 0);
      currentTasksRangeStartDate.setDate(currentTasksRangeStartDate.getDate() - PREVIOUS_DAYS);

      const currentTasksRangeEndDate = new Date(now.getTime());
      currentTasksRangeEndDate.setHours(0, 0, 0);
      currentTasksRangeEndDate.setDate(currentTasksRangeEndDate.getDate() + UPCOMING_DAYS);

      query.due_at = {
        $gte: currentTasksRangeStartDate.toJSON(),
        $lte: currentTasksRangeEndDate.toJSON(),
      };
      break;
    }
    case 'complete': {
      query.status = 'complete';
      break;
    }
    case 'upcoming': {
      query.due_at = {
        $gte: new Date(),
      };
      break;
    }
    default:
    // none
  }
  return query;
};

const applySorting = (query, sortField, sortOrder) => {
  if (sortField && sortOrder === 'desc') {
    query[`$sort[${sortField}]`] = DESCENDING;
  } else if (sortField && sortOrder === 'asc') {
    query[`$sort[${sortField}]`] = ASCENDING;
  } else {
    query['$sort[due_at]'] = DESCENDING;
  }
  return query;
};

const applyPagination = (query, page, pageSize) => {
  query.$limit = pageSize;
  query.$skip = pageSize * (page - 1);
  return query;
};

/**
 * Reconstructs the task's due_at value given a task's due_date and due_time.
 *
 * If the task is all day, default time is 12:00 am. parseFromUserTimeZone
 * parses the concat of the date and time from the USER'S timezone to local.
 * A specific format is expected here, that being YYYY-MM-DD h:mm a.
 *
 * If the task is not all day, call the same parse function on the due date
 * and time, making sure to convert the time to 12h as this format is needed
 * to parse the time properly. If no time has been given, default should be
 * 8:00 am.
 *
 * @param  {Object} task the task
 * @param  {String} keyDate the date key name
 * @param  {String} keyTime the time key name
 * @return {Date}        the parsed date or null
 */
const getKeyDateTime = (task, keyDate, keyTime) => {
  if (task.isAllDay) {
    const dueTime = '12:00 am';
    return parseFromUserTimeZone(`${task.dueDate} ${dueTime}`);
  }

  if (task[keyDate]) {
    let dueTime = task[keyTime];
    if (!task[keyTime]) dueTime = '8:00 am';
    if (task[keyTime]) {
      const dueTimeArray = task[keyTime].split(' ');
      if (dueTimeArray && dueTimeArray.length !== 2) {
        const amTime = task[keyTime].split('am');
        if (amTime[1] !== undefined && amTime[1].length === 0) {
          dueTime = `${amTime[0]} am`;
        }
        const pmTime = task[keyTime].split('pm');
        if (pmTime[1] !== undefined && pmTime[1].length === 0) {
          dueTime = `${pmTime[0]} pm`;
        }
      }
    }
    return parseFromUserTimeZone(`${task[keyDate]} ${dueTime}`);
  }
  return null;
};

const blankTask = Object.freeze({
  category: 0,
  description: null,
  notes: null,
  assignedUser: null,
  assignedUsers: [],
  notifyAssignee: false,
  action: 'None',
  contact: null,
  contactsSelected: [],
  dueDate: null,
  endAt: null,
  dueTime: '8:00 am',
  dueEndTime: '9:00 am',
  isAllDay: false,
  repeat: 0,
  folder: 0,
  status: 'incomplete',
  ownerId: null,
  recurrenceRule: '', // recurrenceRule is the RFC5545 standard to record recurrence
  crm_task_id: -1,
  isV21: true,
  is_two_way_sync: false,
  email_account_connected_id: 0,
  // recurrence: {
  //   freq: 'WEEKLY',
  //   interval: 3,
  //   count: 55,
  // },
});

export default {
  namespaced: true,

  state: {
    activeTab: TAB_V21,
    tasks: [],
    totalItems: 0,
    pages: 0,
    categories: [],
    folders: [],
    contactsSelected: [],
    contacts: [],
    assignedUsers: [],
    task: { ...blankTask },
    inProgress: false,
    tasksError: false,
    tasksErrorMessage: '',
    tasksBulkLoading: false,
    tasksBulkLoadingMessage: '',
    isEditTaskDrawerOpen: false,
    buttonDisable: false,
  },

  mutations: {
    setBtnDisable(state, payload) {
      state.buttonDisable = payload;
    },
    setTasks(state, { tasks, totalItems, pageSize }) {
      state.tasks = tasks;
      state.totalItems = totalItems;
      state.pages = Math.ceil(totalItems / pageSize) || 0;
      state.tasksError = false;
    },
    setCategories(state, payload) {
      state.categories = payload;
    },
    setActiveTab(state, { activeTab }) {
      state.activeTab = activeTab;
    },
    setFolders(state, payload) {
      state.folders = payload;
    },
    setContactsSelected(state, payload) {
      state.contactsSelected = payload;
      state.contacts = payload;
      state.tasksBulkLoading = false;
    },
    setContacts(state, payload) {
      state.contacts = payload;
    },
    setTask(state, payload) {
      state.task = payload;

      if (
        state.task.contact &&
        !state.contacts.some(contact => contact.id === state.task.contact.id)
      ) {
        state.contacts.push(state.task.contact);
      }
    },
    setInProgress(state, payload) {
      state.inProgress = payload;
    },
    setTasksError(state, payload) {
      state.tasksError = true;
      state.inProgress = false;
      state.tasksErrorMessage = `${payload.code} ${payload.message}`;
    },
    setTasksBulkLoading(state, { isBulkLoading, tasksAmount = null, isDeletedTask = false }) {
      state.tasksBulkLoading = isBulkLoading;
      if (isDeletedTask) {
        const TASK_AMOUNT = tasksAmount > 1 ? `tasks` : `task`;
        state.tasksBulkLoadingMessage = `Successfully deleted ${tasksAmount} ${TASK_AMOUNT}.`;
      } else {
        const TASK_AMOUNT = tasksAmount > 1 ? `tasks are` : `task is`;
        state.tasksBulkLoadingMessage = `${tasksAmount} ${TASK_AMOUNT} being created.`;
      }
    },
    setTasksErrorManual(state, isError) {
      state.tasksError = isError;
    },
    setTasksLoadingManual(state, isShown) {
      state.tasksBulkLoading = isShown;
    },
    setIsEditTaskOpen(state, isOpen) {
      state.isEditTaskDrawerOpen = isOpen;
    },
  },

  actions: {
    setActiveTab({ commit, rootState }, activeTab) {
      commit('setActiveTab', { activeTab });
    },

    disableEnableBtn({ commit }, taskCopy) {
      if (taskCopy.assignedUser) {
        const val = taskCopy.assignedUser.value === -1 && !taskCopy.contactsSelected.length;
        commit('setBtnDisable', val);
        return;
      }
      commit('setBtnDisable', false);
    },

    resetBtnStateWhenOpenDrawer({ commit }) {
      commit('setBtnDisable', false);
    },

    async migrateAllTasks(
      { commit, rootState },
      { page, statusFilter, teamMemberFilter, sortOrder, sortField, contactIds },
    ) {
      if (!rootState.userInformation.user) return {};
      let query = {};

      query = applyFilters(query, statusFilter);
      if (contactIds && contactIds.length) {
        query.contact_id = contactIds[0];
        query.show_teammate_tasks = true;
      } else {
        query.assigned_user_id = teamMemberFilter || rootState.authentication.currentUser;
      }
      query.$limit = -1;

      let response;
      try {
        response = await feathers.service('tasks').find({ query });
        const ids = response.map(item => item.id);

        commit('setTasksBulkLoading', {
          isBulkLoading: true,
          tasksAmount: ids.length,
        });

        await axios.post(`${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/migrate/tasks`, {
          crm_task_ids: ids,
        });
      } catch (error) {
        commit('setTasksError', error);
        return;
      }
    },

    async findTasks(
      { commit, rootState },
      {
        page, statusFilter, teamMemberFilter, sortOrder, sortField, contactIds,
      },
    ) {
      if (!rootState.userInformation.user) return {};
      let query = {};

      const pageSize = rootState.preferences.tasksPageSize;

      query = applyFilters(query, statusFilter);
      query = applySorting(query, sortField, sortOrder);
      query = applyPagination(query, page, pageSize);

      if (contactIds && contactIds.length > 0) {
        query.contact_id = contactIds[0];
        query.show_teammate_tasks = true;
      } else {
        query.assigned_user_id = teamMemberFilter || rootState.userInformation.user.id;
        query.contact_assignee = true;
      }

      

      let response;
      const activeTab = rootState.tasks.activeTab;
      try {
        if (activeTab === TAB_V1) {
          if (query.contact_assignee) delete query.contact_assignee;
          response = await feathers.service('tasks').find({ query });
        } else {
          query.crm_user_id = teamMemberFilter || rootState.userInformation.user.id;
          
          response = await axios.get(
            `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/scheduled/tasks`,
            {
              params: query,
            },
          );
        }
      } catch (error) {
        if (activeTab === TAB_V1) {
          commit('setTasksError', error);
          return;
        }

        if (error && error.status && error.data && error.data.error) {
          commit('setTasksError', { code: error.status, message: error.data.error });
          return;
        }

        commit('setTasksError', { code: 504, message: `Error getting Tasks, please try again.` });
        return;
      }

      let tasks = [];
      if (activeTab === TAB_V1) {
        tasks = response.data.map(item => {
          const { contact, category, assigned_user_id: assignedUserId } = item;
          let assignedUser = {};
          if (item.assigned_user) {
            assignedUser = {
              first_name: item.assigned_user.first_name,
              last_name: item.assigned_user.last_name,
              is_active: item.assigned_user.is_active,
              is_on_team: item.assigned_user.is_on_team,
            };
          }

          const taskList = {
            id: item.id,
            due_at: item.due_at,
            description: item.description,
            contact_id: contact ? contact.id : '',
            contact_name: contact ? `${contact.first_name} ${contact.last_name}` : '',
            contact_mobile_phone: contact ? contact.mobile_phone : '',
            contact,
            assigned_user: assignedUser,
            assigned_user_id: assignedUserId,
            notes: item.notes,
            isV21: false,
            category: category || categoryNone,
            is_campaign_task: !!item.campaign_contact_id,
          };
          return taskList;
        });
      } else {
        response = response.data;
        tasks = response.data.map(item => {
          const { contact, category, assigned_user_id: assignedUserId } = item;
          let assignedUser = {};
          if (item.assigned_user) {
            assignedUser = {
              first_name: item.assigned_user.first_name,
              last_name: item.assigned_user.last_name,
              is_active: item.assigned_user.is_active,
              is_on_team: item.assigned_user.is_on_team,
            };
          }

          const taskList = {
            id: item.id,
            due_at: item.due_at,
            end_at: item.end_at,
            description: item.description,
            contact_id: contact ? contact.id : '',
            contact_name: contact ? `${contact.first_name} ${contact.last_name}` : '',
            contact_mobile_phone: contact ? contact.mobile_phone : '',
            contact,
            assigned_user: assignedUser,
            assigned_user_id: assignedUserId,
            notes: item.notes,
            category: category || categoryNone,
            is_campaign_task: item.action === TASKS_ACTIONS.campaign,
            is_nylas_task: item.action === TASKS_ACTIONS.nylas,
            crm_task_id: item.parent_id,
            isV21: true,
            isRecurrent: item.is_recurrent,
          };

          return taskList;
        });
      }

      const totalItems = response ? response.total : 0;
      commit('setTasks', { tasks, totalItems, pageSize });
    },

    async findCategories({ commit }) {
      let response;
      try {
        response = await feathers.service('task-categories').find({ query: { $paginate: false } });
      } catch (error) {
        commit('setTasksError', error);
      }

      const categories = response;

      categories.unshift(categoryNone);
      commit('setCategories', categories);
    },

    async findFolders({ commit }) {
      let response;
      try {
        response = await feathers.service('task-folders').find();
      } catch (error) {
        commit('setTasksError', error);
      }

      const folders = response.data;
      // todo bk: this should live somewhere else
      const folderNone = { id: 0, name: 'None', rank: 0 };
      folders.unshift(folderNone);
      commit('setFolders', folders);
    },

    async findContactsSelected({ commit, rootState }, contactIds) {
      const query = {};
      if (!contactIds || !contactIds.length) {
        return;
      }

      // This service call caps out of contactIds is too large
      if (contactIds.length <= MAX_CONTACT_ARRAY_LENGTH) {
        query.id = {};
        query.id.$in = contactIds;
        query.$teams = rootState.contactsTable.shouldShowTeamMembersContacts;
        query.$limit = MAX_CONTACT_ARRAY_LENGTH;
        let response;

        try {
          response = await feathers.service('contacts').find({ query, headers: ESSearchHeader });
        } catch (error) {
          commit('setTasksError', error);
        }

        const contacts = response.data.map(contact => {
          return {
            id: contact.id,
            name: formatContactName(contact),
          };
        });

        commit('setContactsSelected', contacts);
      } else {
        query.$paginate = 'false';
        let response;

        try {
          response = await feathers.service('cached-contacts').find({
            query,
            headers: ESSearchHeader,
          });
        } catch (error) {
          commit('setTasksError', error);
        }
        response = response.filter(contact => contactIds.includes(contact.id));
        const contacts = response.map(contact => {
          return {
            id: contact.id,
            name: formatContactName(contact),
          };
        });

        commit('setContactsSelected', contacts);
      }
    },
    setAllContactsSelected({ commit }, contacts) {
      const formattedContacts = contacts.map(contact => {
        return {
          id: contact.id,
          name: formatContactName(contact),
        };
      });
      commit('setContactsSelected', formattedContacts);
    },
    async findContacts({ commit }, query) {
      if (query === null) return;

      const likeQuery = {
        $or: [{ first_name: { $like: `%${query}%` } }, { last_name: { $like: `%${query}%` } }],
        $limit: 50,
      };
      let response;
      try {
        response = await feathers
          .service('contacts')
          .find({ query: likeQuery, headers: ESSearchHeader });
      } catch (error) {
        commit('setTasksError', error);
      }

      const contacts = response.data.map(contact => {
        return {
          id: contact.id,
          name: contact ? `${contact.first_name} ${contact.last_name}` : '',
        };
      });

      commit('setContacts', contacts);
    },

    async getTask({ commit, rootState }, taskEdited) {
      const newBlankTask = { ...blankTask };
      if (!taskEdited || !taskEdited.id) {
        commit('setTask', newBlankTask);
        return;
      }

      const { id } = taskEdited;
      commit('setInProgress', true);
      try {
        let taskData = {};
        if (taskEdited.isV21) {
          const response = await axios.get(
            `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/scheduled/tasks/${id}`,
          );
          taskData = response.data.manual_event;
          taskData.eventData = response.data.event;
        } else {
          taskData = await feathers.service('tasks').get(id);
        }

        let task = {};
        task.description = taskData.description;
        task.notes = taskData.notes;
        task.isAllDay = !!taskData.is_all_day.Bool;
        task.action = taskData.action_type;
        task.status = taskData.status;
        task.category = taskData.category;
        task.folder = taskData.folder;
        task.taskTypeId = taskData.task_type_id;
        task.ownerId = taskData.owner_user_id;

        if (taskEdited.isV21) {
          const actionType =
            Object.keys(ACTION_TYPES).find(k => ACTION_TYPES[k] === taskData.reminder_type) || '';
          task.scheduledEventId = taskData.eventData.scheduled_event_id;
          task.recurrenceRule = taskData.eventData.recurrenceRule;
          task.isRecurrent = taskData.is_recurrent;
          task.action = actionType;
          task.notifyAssignee = taskData.notify_assignee;
          task.crm_task_id = taskData.parent_id;
          task.is_campaign_task = taskData.campaign_id > 0;
          task.is_nylas_task = taskData.action === TASKS_ACTIONS.nylas;
          task.isV21 = true;
          task.keepInSync = task.is_nylas_task;
          task.email_account_connected_id = taskData.eventData.email_account_connected_id.Int32;
          task.is_two_way_sync = taskData.is_two_way_sync;
        }

        if (taskData.due_at) {
          task.dueDate = convertToYearMonthDay(taskData.due_at);
          task.dueTime = convertTime12(taskData.due_at);
        }

        if (taskData.end_at) {
          task.endAt = convertToYearMonthDay(taskData.end_at);
          task.dueEndTime = convertTime12(taskData.end_at);
        }

        if (taskData.assigned_user) {
          const { assigned_user: assignedUser } = taskData;
          task.assignedUser = {};
          task.assignedUser.value = taskData.assigned_user_id;
          task.assignedUser.text = `${assignedUser.first_name} ${assignedUser.last_name}`;
          task.assignedUsers = [task.assignedUser];
        }

        if (taskData.contact) {
          const { contact } = taskData;
          task.contact = {};
          task.contact.id = taskData.contact.id;
          task.contact.name = `${contact.first_name} ${contact.last_name}`;
          task.contactsSelected = [task.contact];
        }

        commit('setContactsSelected', task.contactsSelected);
        commit('setTask', task);
      } catch (error) {
        commit('setTasksError', error);
      }
      commit('setInProgress', false);
    },

    async saveTask({ commit, rootState }, { id, task }) {
      commit('setTasksLoadingManual', false);
      commit('setTasksErrorManual', false);
      const folderId = task.folder ? task.folder.id : 0;
      const categoryId = task.category ? task.category.id : 0;
      const contactId = task.contact ? task.contact.id : 0;
      const assignedUserId = task.assignedUser
        ? task.assignedUser.value
        : rootState.userInformation.user.id;

      if (id) {
        try {
          const payload = {
            description: task.description,
            notes: task.notes,
            due_at: getKeyDateTime(task, 'dueDate', 'dueTime'),
            action_type: task.action,
            status: task.status,
            assigned_user_id: assignedUserId,
            task_type_id: task.taskTypeId,
            is_all_day: task.isAllDay,
            folder_id: folderId,
            category_id: categoryId,
            contact_id: contactId,
            notify_assignee: task.notifyAssignee,
            reminder_type: ACTION_TYPES[task.action],
            recurrence_rule: task.recurrenceRule,
            update_type: task.updateType,
            is_two_way_sync: task.is_two_way_sync,
            end_at: getKeyDateTime(task, 'endAt', 'dueEndTime'),
          };
          let resource = task.is_campaign_task
            ? TASKS_ACTIONS.campaign
            : task.is_nylas_task
            ? TASKS_ACTIONS.nylas
            : TASKS_ACTIONS.calendar;
          if (payload.keep_in_sync) {
            resource = TASKS_ACTIONS.nylas;
          }
          await axios.put(
            `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/scheduled/${resource}/${id}`,
            payload,
          );
        } catch (error) {
          commit('setTasksError', error);
        }
      } else {
        try {
          const payload = {
            description: task.description,
            notes: task.notes,
            due_at: getKeyDateTime(task, 'dueDate', 'dueTime'),
            action_type: task.action,
            status: task.status,
            assigned_user_id: assignedUserId,
            task_type_id: task.taskTypeId,
            is_all_day: task.isAllDay,
            folder_id: folderId,
            category_id: categoryId,
            contact_id: contactId,
            notify_assignee: task.notifyAssignee,
            reminder_type: ACTION_TYPES[task.action],
            recurrence_rule: task.recurrenceRule,
            end_at: getKeyDateTime(task, 'endAt', 'dueEndTime'),
            is_two_way_sync: task.is_two_way_sync,
          };
          if (task.emailAccountConnected) {
            payload.email_account_connected_id = task.emailAccountConnected.id;
          }
          let resource = TASKS_ACTIONS.calendar;
          if (payload.is_two_way_sync) {
            resource = TASKS_ACTIONS.nylas;
          }
          const response = await axios.post(
            `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/scheduled/${resource}`,
            payload,
          );
          commit('setTasksBulkLoading', {
            isBulkLoading: true,
            tasksAmount: response.data.events_created,
          });
        } catch (error) {
          commit('setTasksError', error);
        }
        commit('setTask', blankTask);
      }
    },

    async processBulkTask({ commit, rootState, rootGetters }, { task }) {
      commit('setTasksLoadingManual', false);
      commit('setTasksErrorManual', false);
      const { contactsTable } = rootState;

      const folderId = task.folder ? task.folder.id : 0;
      const categoryId = task.category ? task.category.id : 0;
      const assignedUserId = task.assignedUser
        ? task.assignedUser.value
        : rootState.userInformation.user.id;
      const data = {
        action: {
          type: 'CREATE',
          resource: 'tasks',
          payload: {
            description: task.description,
            notes: task.notes,
            due_at: getKeyDateTime(task, 'dueDate', 'dueTime'),
            action_type: task.action,
            status: task.status,
            assigned_user_id: assignedUserId,
            task_type_id: task.taskTypeId,
            is_all_day: task.isAllDay,
            folder_id: folderId,
            category_id: categoryId,
            notify_assignee: task.notifyAssignee,
            reminder_type: ACTION_TYPES[task.action],
            recurrence_rule: task.recurrenceRule,
            end_at: getKeyDateTime(task, 'endAt', 'dueEndTime'),
            is_two_way_sync: task.is_two_way_sync,
          },
        },
      };
      if (task.emailAccountConnected) {
        data.action.payload.email_account_connected_id = task.emailAccountConnected.id;
      }

      if (contactsTable.selectedActionType === 'all') {
        const staticListId = contactsTable.selectedEmailListId;
        if (staticListId != null) {
          data.list_id = staticListId;
        } else {
          data.query = rootGetters['contactsTable/queryParamsForAllContacts'];
        }
        if (contactsTable.excludedContactsIds.length > 0) {
          data.excluded_ids = [...contactsTable.excludedContactsIds];
        }
      } else {
        data.ids = task.contactsSelected.map(contactSelected => {
          return contactSelected.id;
        });
      }

      try {
        await axios({
          url: `${process.env.VUE_APP_CONTACTS_MS_BASE_URL}/bulk`,
          method: 'POST',
          data,
        });
      } catch (error) {
        commit('setTasksError', error);
      }
      commit('setTask', blankTask);
    },

    async editTaskStatus({ commit, dispatch, rootState }, { taskChange, status }) {
      if (!taskChange || !taskChange.id) {
        return;
      }

      const payload = { status };

      try {
        if (taskChange.isV21) {
          payload.update_type = 3;
          await axios.put(
            `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/support/tasks/${taskChange.id}`,
            payload,
          );
        } else {
          await feathers.service('tasks').patch(taskChange.id, payload);
        }
      } catch (error) {
        commit('setTasksError', error);
      }
    },

    async migrateTasks({ commit, dispatch, rootState }, { ids }) {
      if (!ids || !ids.length) {
        return;
      }

      try {
        await axios.post(`${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/migrate/tasks`, {
          crm_task_ids: ids,
        });
      } catch (error) {
        commit('setTasksError', error);
      }
    },

    async deleteTask({ commit, dispatch, rootState }, { id, deleteType }) {
      if (!id) {
        return;
      }

      const {
        userInformation: {
          user: { feature_flags },
        },
        tasks: { activeTab },
      } = rootState;
      try {
        if (activeTab === TAB_V1) {
          await feathers.service('tasks').remove(id);
        } else {
          await axios.delete(
            `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/support/tasks/${id}?delete_type=${deleteType}`,
          );
        }
        commit('setTasksLoadingManual', false);
      } catch (error) {
        commit('setTasksError', error);
      }
    },

    setInProgress({ commit, dispatch }, isInProgress) {
      commit('setInProgress', isInProgress);
    },

    resetTask({ commit, dispatch }) {
      commit('setTask', blankTask);
    },

    setTasksBulkLoading({ commit, dispatch }, { isBulkLoading, tasksAmount = null }) {
      commit('setTasksBulkLoading', { isBulkLoading, tasksAmount });
    },

    setTasksErrorManual({ commit }, payloadError) {
      commit('setTasksError', payloadError);
      commit('setTasksErrorManual', payloadError.isError);
    },

    cleanTasks({ commit }) {
      commit('setTasks', { tasks: [], totalItems: 0, pageSize: 0 });
      commit('setTasksBulkLoading', {
        isBulkLoading: false,
        tasksAmount: 0,
      });
    },
    setDeletedTask({ commit }, deletedTasks) {
      commit('setTasksBulkLoading', {
        isBulkLoading: true,
        tasksAmount: deletedTasks,
        isDeletedTask: true,
      });
    },

    resetTaskLoadingState({ commit }) {
      commit('setTasksLoadingManual', false);
      commit('setTasksErrorManual', false);
    },
  },
};
